import { useEffect } from 'react';

import { useBackground } from '~/components/Providers/BackgroundProvider';
import { getFullLevelThumbnail, seo } from '~/util';
import { countryProfileQuery } from '~/util/queries/public/profiles';
import CountryProfileSummary from './-CountryProfileSummary';
import CountryProfileMain from './-CountryProfileMain';
import { createFileRoute, stripSearchParams } from '@tanstack/react-router';

import {
    countryParamsSchema,
    defaultCountrySearchParams,
} from './-SearchParams';
import { fromNumeric, getCountryFlagUrl } from '~/util/countries';
import { NotFoundComponent } from '~/components/DefaultNotFound';

export const Route = createFileRoute('/profile/country/$country')({
    validateSearch: countryParamsSchema,
    search: {
        middlewares: [stripSearchParams(defaultCountrySearchParams)],
    },
    loaderDeps: ({ search: { list } }) => ({ list }),
    loader: async ({
        context: { queryClient },
        params: { country },
        deps: { list },
    }) => {
        const [classic, platformer] = await Promise.all([
            queryClient.fetchQuery(
                countryProfileQuery({ list: 'classic', country }),
            ),
            queryClient.fetchQuery(
                countryProfileQuery({ list: 'platformer', country }),
            ),
        ]);

        const classicTotal = (
            classic?.records?.filter(
                (record) =>
                    record.level.status == 'MainList' ||
                    record.level.status == 'Pending',
            ) ?? []
        ).length;

        const platformerTotal = (
            platformer?.records?.filter(
                (record) =>
                    record.level.status == 'MainList' ||
                    record.level.status == 'Pending',
            ) ?? []
        ).length;

        return {
            profile: list === 'classic' ? classic : platformer,
            total: list === 'classic' ? classicTotal : platformerTotal,
            classicTotal,
            platformerTotal,
        };
    },
    head: ({ loaderData }) => {
        const { profile } = loaderData || {};
        const countryData = profile?.country
            ? fromNumeric(profile?.country)
            : undefined;
        return {
            meta: seo({
                title: `${countryData?.country || 'Unknown Country'}'s Profile`,
                image: countryData ? getCountryFlagUrl(countryData) : undefined,
                imageAlt: countryData?.country || 'Unknown Country',
            }),
        };
    },

    component: CountryProfile,
    notFoundComponent: () =>
        NotFoundComponent({ customNotFound: 'Country not found' }),
});
function CountryProfile() {
    const { setBackground, setOpacity, resetBackground } = useBackground();
    const { profile } = Route.useLoaderData({
        select: ({ profile }) => ({ profile }),
    });

    useEffect(() => {
        if (profile?.records && profile.records[0]?.level?.level_id) {
            resetBackground();
            setBackground(
                getFullLevelThumbnail(profile.records[0].level.level_id),
            );
            setOpacity('0.05');
        }
    }, [profile, setBackground, setOpacity, resetBackground]);

    useEffect(() => {
        return resetBackground;
    }, [resetBackground]);

    return (
        <main className="h-full flex justify-center">
            <div className="flex flex-col items-center gap-4 w-full h-full bg-background-dark rounded-2xl py-4">
                <div className="flex flex-col lg:flex-row w-full  justify-center items-center lg:items-start lg:justify-start">
                    <CountryProfileSummary />
                    <CountryProfileMain />
                </div>
            </div>
        </main>
    );
}
